/**
 * Hotjar Analytics Utility
 * Centralized Hotjar initialization and tracking
 */

import { hotjar } from 'react-hotjar';

// Track if Hotjar has been initialized
let isInitialized = false;

/**
 * Initialize Hotjar if configured
 * Only initializes in production with valid credentials
 */
export const initializeHotjar = () => {
  // Get configuration from environment variables
  const hotjarId = process.env.REACT_APP_HOTJAR_ID;
  const hotjarVersion = process.env.REACT_APP_HOTJAR_VERSION;

  // Skip if already initialized
  if (isInitialized) {
    console.log('Hotjar already initialized');
    return;
  }

  // Skip if not configured
  if (!hotjarId || !hotjarVersion) {
    console.log('Hotjar not configured - skipping initialization');
    return;
  }

  try {
    // Convert to numbers, using string conversion to prevent minification
    const hjid = parseInt(String(hotjarId), 10);
    const hjsv = parseInt(String(hotjarVersion), 10);

    if (isNaN(hjid) || isNaN(hjsv)) {
      console.warn('Invalid Hotjar configuration');
      return;
    }

    console.log('Initializing Hotjar with ID:', hjid);
    hotjar.initialize(hjid, hjsv);
    isInitialized = true;
    console.log('Hotjar initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Hotjar:', error);
  }
};

/**
 * Check if Hotjar is properly initialized
 */
export const isHotjarReady = () => {
  try {
    return isInitialized && hotjar.initialized();
  } catch (error) {
    return false;
  }
};

/**
 * Track a page view in Hotjar
 * @param {string} path - The page path
 */
export const trackPageView = (path) => {
  if (!isHotjarReady()) {
    return;
  }

  try {
    console.log('Hotjar tracking page:', path);
    hotjar.stateChange(path);
  } catch (error) {
    console.error('Hotjar page tracking error:', error);
  }
};

/**
 * Identify a user in Hotjar
 * @param {string} userId - The user's unique ID
 * @param {object} userAttributes - Additional user attributes
 */
export const identifyUser = (userId, userAttributes = {}) => {
  if (!isHotjarReady()) {
    return;
  }

  if (!userId) {
    console.warn('Hotjar: Cannot identify user without userId');
    return;
  }

  try {
    console.log('Hotjar identifying user:', userId);
    hotjar.identify(userId, {
      email: userAttributes.email || '',
      ...userAttributes,
    });
  } catch (error) {
    console.error('Hotjar user identification error:', error);
  }
};

/**
 * Trigger a Hotjar event
 * @param {string} eventName - The event name
 */
export const trackEvent = (eventName) => {
  if (!isHotjarReady()) {
    return;
  }

  try {
    console.log('Hotjar tracking event:', eventName);
    if (window.hj) {
      window.hj('event', eventName);
    }
  } catch (error) {
    console.error('Hotjar event tracking error:', error);
  }
};

export default {
  initializeHotjar,
  isHotjarReady,
  trackPageView,
  identifyUser,
  trackEvent,
};

